import { angleDelta, clamp, type V2 } from '../core/math';
import type { RayHit, RaycastOptions } from '../physics/types';
import { KNOCKDOWN_DAMAGE, KNOCKDOWN_HP, type FirearmSpec, type MeleeSpec } from './weaponData';

/**
 * Pure combat maths: who a swing reaches, who the aim locks onto, where pellets go, what a bullet
 * hits first (buildings and cars stop it: a physics raycast), and what a hit does to a person.
 * No services; the weapons system gathers the targets and applies the results.
 */

/**
 * Something with a body you can hit: a pedestrian, a foot officer, or (online) another player. A
 * 'vehicle' is only ever an AIM target (the lock-on): it is never in the lists melee and bullets are
 * traced against (cars are physics bodies, so a shot at one hits the body itself).
 */
export interface CombatTarget {
  kind: 'ped' | 'cop' | 'player' | 'vehicle';
  /** Ped id, police unit id, the player's server entity id, or the vehicle id. */
  id: number;
  x: number;
  z: number;
  /** Hit radius (m). */
  r: number;
  /** Lying down (knocked or dead): melee and lock-on skip them; a stray bullet can still land. */
  down: boolean;
  /** Vehicles: nobody at the wheel and standing still (a parked car). The automatic lock never picks one. */
  empty?: boolean;
}

/**
 * Automatic lock-on ranks a car below every person: this is added to a vehicle's score (metres), more
 * than any range a gun has, so a parked Cortina next to a man never takes the lock from him.
 */
export const VEHICLE_LOCK_PENALTY = 1000;

export const TARGET_RADIUS = 0.42;
/** Lock-on: half-angle of the forward cone (rad) a new target must be in. */
export const LOCK_CONE = 0.7;
/** An existing lock survives this far out of the cone (the player turns to face it anyway). */
export const LOCK_KEEP_CONE = 1.4;
/** Melee targets in reach + this get the player turned to face them (assist). */
export const MELEE_ASSIST = 0.9;
export const MELEE_ASSIST_CONE = 1.0;

/** Direction angle (heading convention: 0 = +Z, atan2(x, z)) from a to b. */
export function headingTo(a: V2, b: V2): number {
  return Math.atan2(b.x - a.x, b.z - a.z);
}

/**
 * Nearest standing target inside the forward cone and range with a clear line (`los`), preferring
 * ones nearer the centre line. People always come first: a car is only picked when no person is in
 * the cone, and an empty, parked one never is. `keep` (the current lock) survives a wider cone and a
 * little more range — unless it is a car and a person has stepped into the cone.
 */
export function pickLockTarget(
  from: V2,
  heading: number,
  targets: readonly CombatTarget[],
  range: number,
  los: (a: V2, b: V2) => boolean,
  keep: CombatTarget | null = null,
  cone = LOCK_CONE,
  keepCone = LOCK_KEEP_CONE,
): CombatTarget | null {
  const keepOk = (k: CombatTarget | null): k is CombatTarget => {
    if (!k || k.down) return false;
    const d = Math.hypot(k.x - from.x, k.z - from.z);
    const off = Math.abs(angleDelta(heading, headingTo(from, k)));
    return d <= range * 1.15 && off <= keepCone && los(from, k);
  };
  if (keep && keep.kind !== 'vehicle' && keepOk(keep)) return keep;
  let best: CombatTarget | null = null;
  let bestScore = Infinity;
  for (const t of targets) {
    if (t.down || (t.kind === 'vehicle' && t.empty)) continue;
    const d = Math.hypot(t.x - from.x, t.z - from.z);
    if (d > range || d < 0.05) continue;
    const off = Math.abs(angleDelta(heading, headingTo(from, t)));
    if (off > cone) continue;
    // Distance matters most; being off the centre line costs like ~6 m per radian. Cars rank last.
    const score = d + off * 6 + (t.kind === 'vehicle' ? VEHICLE_LOCK_PENALTY : 0);
    if (score >= bestScore) continue;
    if (!los(from, t)) continue;
    best = t;
    bestScore = score;
  }
  // A car we were locked on stays ours unless a person took over.
  if (keep && keep.kind === 'vehicle' && (!best || best.kind === 'vehicle') && keepOk(keep)) return keep;
  return best;
}

/**
 * Free aim (mouse cursor, a pad's right stick): the lock only snaps onto somebody this close (rad) to
 * where the player points, and lets go past FREE_AIM_KEEP_CONE; with nobody there the shot goes exactly
 * that way. Much narrower than the facing cone, so the cursor / stick decides who.
 */
export const FREE_AIM_CONE = 0.22;
export const FREE_AIM_KEEP_CONE = 0.4;
/** A right stick pushed less than this (after the dead zone) does not aim. */
export const AIM_STICK_MIN = 0.35;

/**
 * Heading (0 = +Z, atan2(x, z)) a screen-relative stick points along: `x` right, `y` up the screen,
 * on the ground through the camera's screen axes. Null when the stick is (nearly) centred.
 */
export function stickAimHeading(x: number, y: number, screenUp: V2, screenRight: V2, min = AIM_STICK_MIN): number | null {
  if (Math.hypot(x, y) < min) return null;
  return Math.atan2(screenUp.x * y + screenRight.x * x, screenUp.z * y + screenRight.z * x);
}

/**
 * A lock the player chose by tapping (or right-clicking) someone: it holds without the cone (the
 * player need not face them) while they stand, stay within reach and in sight.
 */
export function keepChosenLock(from: V2, t: CombatTarget | null, range: number, los: (a: V2, b: V2) => boolean): boolean {
  if (!t || t.down) return false;
  return Math.hypot(t.x - from.x, t.z - from.z) <= range * 1.15 && los(from, t);
}

/** Who a melee blow lands on: the nearest standing target within reach and the arc (null = air). */
export function meleeTarget(from: V2, heading: number, spec: MeleeSpec, targets: readonly CombatTarget[]): CombatTarget | null {
  let best: CombatTarget | null = null;
  let bestD = Infinity;
  for (const t of targets) {
    if (t.down) continue;
    const d = Math.hypot(t.x - from.x, t.z - from.z) - t.r;
    if (d > spec.reach) continue;
    const off = Math.abs(angleDelta(heading, headingTo(from, t)));
    // Very close bodies count even a bit outside the arc (you can't miss someone you're touching).
    if (off > spec.arc && d > 0.35) continue;
    if (d < bestD) {
      bestD = d;
      best = t;
    }
  }
  return best;
}

/** A target to turn towards before swinging (a little wider and longer than the blow itself). */
export function meleeAssist(from: V2, heading: number, spec: MeleeSpec, targets: readonly CombatTarget[]): CombatTarget | null {
  let best: CombatTarget | null = null;
  let bestD = Infinity;
  for (const t of targets) {
    if (t.down) continue;
    const d = Math.hypot(t.x - from.x, t.z - from.z);
    if (d > spec.reach + MELEE_ASSIST) continue;
    if (Math.abs(angleDelta(heading, headingTo(from, t))) > MELEE_ASSIST_CONE) continue;
    if (d < bestD) {
      bestD = d;
      best = t;
    }
  }
  return best;
}

/**
 * Pellet headings for one shot: spread evenly across the cone (so a sawn-off always covers its
 * width) with jitter from `rand` (0..1). `spreadScale` widens it (hip fire, running).
 */
export function pelletHeadings(heading: number, spec: FirearmSpec, rand: () => number, spreadScale = 1): number[] {
  const out: number[] = [];
  const s = spec.spread * spreadScale;
  const n = spec.pellets;
  for (let i = 0; i < n; i++) {
    const even = n === 1 ? 0 : -1 + (2 * i) / (n - 1);
    const jitter = (rand() * 2 - 1) * (n === 1 ? 1 : 1 / (n - 1));
    out.push(heading + clamp(even + jitter, -1, 1) * s);
  }
  return out;
}

/** Damage of one projectile after `distance` metres. */
export function damageAt(spec: FirearmSpec, distance: number): number {
  if (distance <= spec.falloffStart) return spec.damage;
  const k = clamp((distance - spec.falloffStart) / Math.max(1e-3, spec.range - spec.falloffStart), 0, 1);
  return spec.damage * (1 - 0.65 * k);
}

/** Minimal physics surface needed to trace a shot (PhysicsWorld satisfies it). */
export interface RayCaster {
  raycast(origin: V2, dir: V2, maxDist: number, opts?: RaycastOptions): RayHit | null;
}

export interface ShotTrace {
  /** What stopped it. */
  hit: 'target' | 'vehicle' | 'static' | 'none';
  target: CombatTarget | null;
  /** Vehicle body id (hit === 'vehicle'). */
  bodyId: number | null;
  /** The static's tag (hit === 'static': 'building:12', 'wall:railing', 'prop:lamp'…), for the impact kit (src/fx/impactFx.ts). */
  label: string | null;
  point: V2;
  /** Surface normal (static / vehicle hits), else the reverse of the shot. */
  normal: V2;
  distance: number;
}

/**
 * Trace one projectile: the physics raycast finds the first wall / building / car (`mask` = dynamic
 * layers that stop bullets); people are circles tested along the same ray, and only count if they
 * are closer than whatever the ray hit. You can't shoot through buildings. `opts` narrows the statics
 * (online claims use `sightOnly`: walls and buildings, not lamp posts, like the server's colliders).
 */
export function traceShot(physics: RayCaster, from: V2, heading: number, range: number, targets: readonly CombatTarget[], mask: number, opts?: RaycastOptions): ShotTrace {
  const dir = { x: Math.sin(heading), z: Math.cos(heading) };
  const ray = physics.raycast(from, dir, range, { ...opts, mask });
  let block = ray ? ray.distance : range;
  let target: CombatTarget | null = null;
  for (const t of targets) {
    const d = rayCircle(from, dir, t.x, t.z, t.r);
    if (d !== null && d < block) {
      block = d;
      target = t;
    }
  }
  const point = { x: from.x + dir.x * block, z: from.z + dir.z * block };
  if (target) return { hit: 'target', target, bodyId: null, label: null, point, normal: { x: -dir.x, z: -dir.z }, distance: block };
  if (ray) {
    return { hit: ray.bodyId !== null ? 'vehicle' : 'static', target: null, bodyId: ray.bodyId, label: ray.label ?? null, point, normal: { x: ray.normal.x, z: ray.normal.z }, distance: block };
  }
  return { hit: 'none', target: null, bodyId: null, label: null, point, normal: { x: -dir.x, z: -dir.z }, distance: block };
}

/** Distance along a unit ray to a circle (null = miss). A ray starting inside reports 0. */
export function rayCircle(o: V2, d: V2, cx: number, cz: number, r: number): number | null {
  const mx = o.x - cx;
  const mz = o.z - cz;
  const b = mx * d.x + mz * d.z;
  const c = mx * mx + mz * mz - r * r;
  if (c <= 0) return 0;
  if (b > 0) return null;
  const disc = b * b - c;
  if (disc < 0) return null;
  return -b - Math.sqrt(disc);
}

export interface HitResult {
  hp: number;
  /** Goes down (or is already down and stays there). */
  knocked: boolean;
  killed: boolean;
}

/**
 * Apply `damage` to someone with `hp` left. Non-lethal weapons never take the last point; a hard
 * enough blow or low enough health puts you on the floor; nothing left kills (lethal weapons only).
 */
export function resolveHit(hp: number, damage: number, lethal: boolean): HitResult {
  let next = hp - damage;
  if (!lethal) next = Math.max(1, next);
  const killed = next <= 0;
  const knocked = killed || damage >= KNOCKDOWN_DAMAGE || next <= KNOCKDOWN_HP;
  return { hp: Math.max(0, next), knocked, killed };
}
